import { useMemo } from 'react'

import { getNextSignupRoute } from '@pages/Signup/routes/getNextSignupRoute'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'

import { isDepositBeforeKYC } from '@utils/AccountAccess/accountKYCStatuses'
import {
  isEconomicProfileTestNeeded,
  showApptestRiskScreen,
} from '@utils/AccountAccess/accountProductStatuses'

import { AccountDetailedDto, isVerificationExpedited } from '@model/AccountDetailedDto'
import {
  isAccountActivatedStatus,
  isAccountPendingDocumentVerificationStatus,
} from '@model/AccountDetailedStatus'
import { TickmillProductType } from '@model/TickmillProductType'

import { safeLocalStorage } from '@hooks/useStorage'

enum FailReason {
  Test = 'test',
  Signup = 'signup',
}

interface FinancialInfoNextRouteOptions {
  account: AccountDetailedDto | undefined
  product: TickmillProductType
  isLoadingTest: boolean
  isSuccess: boolean
  isSignupFail: boolean
  isTestFail: boolean
}

type UseFinancialInfoNextRouteOptions = Omit<FinancialInfoNextRouteOptions, 'account' | 'product'>

export const getFinancialInfoNextRoute = ({
  account,
  product,
  isLoadingTest,
  isSuccess,
  isSignupFail,
  isTestFail,
}: FinancialInfoNextRouteOptions): string | undefined => {
  if (isSignupFail) {
    return `fail/${FailReason.Signup}`
  }

  if (isTestFail) {
    return `fail/${FailReason.Test}`
  }

  const isCompletedAccountStatus =
    !isLoadingTest &&
    (isAccountActivatedStatus(account) ||
      isAccountPendingDocumentVerificationStatus(account?.status.id))

  if (!isSuccess && !isCompletedAccountStatus) {
    return undefined
  }

  if (showApptestRiskScreen(account, product)) {
    return 'risk'
  }

  if (isDepositBeforeKYC(account) || isVerificationExpedited(account)) {
    return getNextSignupRoute(account)
  }

  if (isEconomicProfileTestNeeded(account, product)) {
    return `/sign-up/economic-profile/${account?.id}`
  }

  if (safeLocalStorage.getItem('hasAdditionalId')) {
    return '/sign-up/confirm'
  }

  return 'complete'
}

export const useFinancialInfoNextRoute = ({
  isLoadingTest,
  isSuccess,
  isSignupFail,
  isTestFail,
}: UseFinancialInfoNextRouteOptions): string | undefined => {
  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()

  return useMemo(
    () =>
      getFinancialInfoNextRoute({
        account,
        product,
        isLoadingTest,
        isSuccess,
        isSignupFail,
        isTestFail,
      }),
    [account, isLoadingTest, isSignupFail, isSuccess, isTestFail, product]
  )
}
